import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faTimes,
  faChevronDown,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons"
import {
  bodyParts,
  BREAST_BODY_PART,
  WHOLE_BODY_BODY_PART,
  WHOLE_BODY_STORED_BODY_PART,
  getBodyPartLabel,
  breastViews,
  breastViewTypes,
  sourceTypes,
  sourceOptions,
  MAMMOGRAM_LOG_TYPE,
} from "../../constants"
import Dropdown from "../../components/Dropdown"
import {
  useAllIllustrationLogs,
  useIllustrationLogsByAccessionNo,
} from "../../hooks/useIllustrationLogs"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import { getDateRange } from "../../utils/datetime"
import { safeParseAiResponse } from "../../utils"
import ReactJson from "react-json-view"
import ReportDrawer from "./components/ReportDrawer"

const sliceBreastAiResponse = (aiResponse, viewType) => {
  if (!aiResponse) return null
  const view = (viewType || "").toLowerCase()
  if (view === breastViewTypes.DIAGNOSTIC) return aiResponse.mammogram ?? null
  if (view === breastViewTypes.ULTRASOUND) return aiResponse.sono ?? null
  if (view === breastViewTypes.BIOPSY) return aiResponse.biopsy ?? null
  if (view === breastViewTypes.DENSITY)
    return {
      density_type: aiResponse.density_type,
      birads_category: aiResponse.birads_category,
      next_step: aiResponse.next_step,
    }
  return aiResponse
}

const toResponseBlocks = (aiResponse) => {
  if (!aiResponse) return []
  return [{ label: null, data: aiResponse }]
}

const BRAIN_LATERAL_COUP_VIEW = "lateralcoup"

// Drops keys the source response doesn't actually have, so a field the AI
// never returned (e.g. an older response logged before "gender" was added)
// disappears from the card instead of showing as a literal `undefined`.
const omitUndefined = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  )

// Ingestion currently logs one merged AIResponse per brain report (lesion
// fields and coup/contrecoup together, no per-view tag), even though
// brain_illustration_results is already split into one row per view. Slicing
// by the result's own viewType — same approach as sliceBreastAiResponse
// above — gets each card its own fields now, and still works unchanged if
// ingestion ever starts logging the views separately.
const sliceBrainAiResponse = (aiResponse, viewType) => {
  if (!aiResponse) return null
  const view = (viewType || "").toLowerCase().replace(/[^a-z]/g, "")
  const shared = {
    viewType: aiResponse.viewType ?? viewType,
    gender: aiResponse.gender,
  }
  if (view === BRAIN_LATERAL_COUP_VIEW) {
    return omitUndefined({
      ...shared,
      coup: aiResponse.coup,
      contrecoup: aiResponse.contrecoup,
    })
  }
  return omitUndefined({
    ...shared,
    white_matter_lesions_count: aiResponse.white_matter_lesions_count,
    white_matter_lesions_severity: aiResponse.white_matter_lesions_severity,
    micro_hemorrhages_count: aiResponse.micro_hemorrhages_count,
    micro_hemorrhages_severity: aiResponse.micro_hemorrhages_severity,
  })
}

const getLogMatchKey = (result) =>
  result.bodyPart?.toLowerCase() === "brain"
    ? "brain"
    : result.viewType?.toLowerCase()

const normalizeMatchValue = (value) =>
  (value || "").toLowerCase().replace(/[^a-z]/g, "")

// Some AI responses carry their own spine_region (e.g. "C-Spine"), either at
// the top level or nested under final_answer depending on the response
// format's age; older/other formats never include it at all.
const getLogSpineRegion = (log) => {
  const parsed = safeParseAiResponse(log.AIResponse)
  const region = parsed?.spine_region ?? parsed?.final_answer?.spine_region
  return region ? normalizeMatchValue(region) : null
}

// Brain AI responses embed their own viewType (e.g. "lateralcoup"), the only
// way to tell apart the Lateral/Anterolateral/Posterolateral/Lateral Coup
// results that otherwise all share the same generic "Brain" LogType.
const getLogBrainViewType = (log) => {
  const parsed = safeParseAiResponse(log.AIResponse)
  return parsed?.viewType ? normalizeMatchValue(parsed.viewType) : null
}

// Whole-body scans merge every table's logs into one list, and knee and spine
// logs both use a "SAGITTAL" LogType. Neither carries a body part, so each
// table's own id field is the only way to keep knee cards on knee logs.
const isKneeResult = (result) => result.kneeIllustrationResultsId !== undefined
const isKneeLog = (log) => log.KneeIllustrationLogID !== undefined

// Neither table carries a region/side key to join illustration_results to
// illustration_logs directly, so a spine study spanning more than one region
// (e.g. c-spine and t-spine), or a brain study spanning more than one view
// (e.g. Lateral and Lateral Coup), can have several logs sharing the same
// LogType. Prefer matching on the AI response's own spine_region/viewType
// when it has one, taking the most recent match — a study reprocessed months
// apart can leave a stale log ahead of today's in plain creation order, which
// throws off a purely positional match. Only when neither is available (e.g.
// Ramsoft's plain final_answer responses) do we fall back to lining results
// and logs up by their shared-type occurrence order — `occurrenceIndex` is
// each result's position among its same-view peers, set in getCardEntries
// below. `claimedRegions` holds the regions its same-view peers carry, so a
// region-less result (e.g. a spondylolisthesis row that failed on modality
// before any AI call) can't fall back onto a log another region already owns.
const findMatchingLog = (
  logsData,
  result,
  occurrenceIndex = 0,
  claimedRegions = new Set()
) => {
  const matches = (logsData || []).filter((log) => {
    const logType = log.LogType?.toLowerCase()
    if (logType === MAMMOGRAM_LOG_TYPE.toLowerCase()) return true
    if (isKneeResult(result) !== isKneeLog(log)) return false
    return logType === getLogMatchKey(result)
  })
  if (!matches.length) return undefined

  const resultRegion = result.spineRegion
    ? normalizeMatchValue(result.spineRegion)
    : null
  if (resultRegion) {
    const byRegion = matches
      .filter((log) => getLogSpineRegion(log) === resultRegion)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    if (byRegion.length) return byRegion[0]
    // The logs are region-tagged but none is for this region (e.g. a T-spine
    // axial result that errored before any AI call was logged) — a positional
    // guess would only hand this card another region's response.
    if (matches.some((log) => getLogSpineRegion(log))) return undefined
  }

  if (result.bodyPart?.toLowerCase() === "brain" && result.viewType) {
    const resultViewType = normalizeMatchValue(result.viewType)
    const byViewType = matches
      .filter((log) => getLogBrainViewType(log) === resultViewType)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    if (byViewType.length) return byViewType[0]
  }

  const ordered = matches
    .filter(
      (log) => resultRegion || !claimedRegions.has(getLogSpineRegion(log))
    )
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
  if (!ordered.length) return undefined
  return ordered[Math.min(occurrenceIndex, ordered.length - 1)]
}

const NewLogs = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [source, setSource] = useQueryState("source", {
    defaultValue: sourceTypes.RAMSOFT,
  })
  const [bodyPart, setBodyPart] = useQueryState("bodyPart", {
    defaultValue: bodyParts.SPINE,
  })
  const [view, setView] = useQueryState("view", {
    defaultValue: "All",
  })

  const handleBodyPartChange = (value) => {
    setBodyPart(value)
    if (value !== bodyParts.SPINE) {
      setView("All")
    }
  }

  const handleSourceChange = (value) => {
    setSource(value)
    if (value !== sourceTypes.OUTSIDE_V3 && bodyPart === BREAST_BODY_PART) {
      setBodyPart(bodyParts.SPINE)
      setView("All")
    }
    if (value !== sourceTypes.RAMSOFT) {
      setReplacedInPACS("All")
    }
  }
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "Today",
  })
  const [status, setStatus] = useQueryState("status", { defaultValue: "All" })
  const [replacedInPACS, setReplacedInPACS] = useQueryState("replacedInPACS", {
    defaultValue: "All",
  })
  const [expandedRow, setExpandedRow] = useState(null)
  const [selectedLogForDrawer, setSelectedLogForDrawer] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const sentinelRef = useRef(null)
  const expandedRowRef = useRef(null)

  const toggleRowExpansion = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId))
  }

  useEffect(() => {
    if (expandedRow) {
      const scrollToRow = () => {
        if (expandedRowRef.current) {
          expandedRowRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }
      }

      setTimeout(scrollToRow, 100)
      setTimeout(scrollToRow, 300)
    }
  }, [expandedRow])

  const getDateFilterValue = (range) => {
    if (range === "All") return ""
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const getIllustrationResults = (log) => {
    const bodyPartKey = `${log.bodyPart?.toLowerCase()}_illustration_results`
    const results = log[bodyPartKey] || []
    const spineResults = [
      ...(log.spine_illustration_results || []),
      ...(log.spine_spondylolisthesis_results || []),
      ...(log.spine_dermatome_results || []),
    ]
    if (log.bodyPart?.toLowerCase() === "spine") return spineResults
    // Whole-body scans produce the spine, brain and knee illustrations.
    if (log.bodyPart?.toLowerCase() === WHOLE_BODY_STORED_BODY_PART) {
      return [
        ...spineResults,
        ...(log.brain_illustration_results || []),
        ...(log.knee_illustration_results || []),
      ]
    }
    return results
  }

  const getDisplayResults = (log) => {
    const results = getIllustrationResults(log)
    if (log.bodyPart?.toLowerCase() !== BREAST_BODY_PART.toLowerCase())
      return results
    const seen = new Set()
    return results.filter((result) => {
      const view = (result.viewType || "").toLowerCase()
      if (seen.has(view)) return false
      seen.add(view)
      return true
    })
  }

  const getCardEntries = (log) => {
    const isBreast =
      log.bodyPart?.toLowerCase() === BREAST_BODY_PART.toLowerCase()

    if (!isBreast) {
      const displayResults = getDisplayResults(log)
      const claimedRegionsByMatchKey = {}
      displayResults.forEach((result) => {
        if (!result.spineRegion) return
        const matchKey = getLogMatchKey(result)
        if (!claimedRegionsByMatchKey[matchKey])
          claimedRegionsByMatchKey[matchKey] = new Set()
        claimedRegionsByMatchKey[matchKey].add(
          normalizeMatchValue(result.spineRegion)
        )
      })

      const occurrenceByMatchKey = {}
      return displayResults.flatMap((result) => {
        const matchKey = getLogMatchKey(result)
        const occurrenceIndex = occurrenceByMatchKey[matchKey] || 0
        occurrenceByMatchKey[matchKey] = occurrenceIndex + 1

        const matchedLog = findMatchingLog(
          logsData,
          result,
          occurrenceIndex,
          claimedRegionsByMatchKey[matchKey]
        )
        const rawAiResponse = safeParseAiResponse(matchedLog?.AIResponse)
        const aiResponse =
          result.bodyPart?.toLowerCase() === "brain"
            ? sliceBrainAiResponse(rawAiResponse, result.viewType)
            : rawAiResponse
        const blocks = toResponseBlocks(aiResponse)
        const cardBlocks = blocks.length
          ? blocks
          : [{ label: null, data: null }]
        return cardBlocks.map((block) => ({ result, block }))
      })
    }

    const results = getIllustrationResults(log)
    const mammogramLog = (logsData || []).find(
      (item) => item.LogType?.toLowerCase() === MAMMOGRAM_LOG_TYPE.toLowerCase()
    )
    const aiResponse = safeParseAiResponse(mammogramLog?.AIResponse)

    return Object.values(breastViewTypes).flatMap((viewType) => {
      const viewRows = results.filter(
        (item) => (item.viewType || "").toLowerCase() === viewType
      )
      const sliced = aiResponse
        ? sliceBreastAiResponse(aiResponse, viewType)
        : null
      const blocks = toResponseBlocks(sliced)

      const result = viewRows[0] || { viewType }
      const block = blocks[0] || { label: null, data: null }
      return [{ result, block }]
    })
  }

  const getIllustrationsAttachedValue = (illustrationResults) => {
    const results = illustrationResults ? illustrationResults : []
    const attachedCount = results.filter(
      (result) => result?.isImageGenerated
    ).length
    const totalCount = results.length

    return {
      value: `${attachedCount}/${totalCount > 0 ? totalCount : 0} attached`,
      success: attachedCount > 0 && attachedCount === totalCount,
    }
  }

  const getAttachedValue = (log) => {
    const isBreast =
      log.bodyPart?.toLowerCase() === BREAST_BODY_PART.toLowerCase()
    if (!isBreast)
      return getIllustrationsAttachedValue(getIllustrationResults(log))

    const cards = getCardEntries(log)
    const attachedViews = new Set(
      cards
        .filter((card) => card.result?.isImageGenerated)
        .map((card) => (card.result.viewType || "").toLowerCase())
    )
    const totalCount = Object.values(breastViewTypes).length
    const attachedCount = attachedViews.size
    return {
      value: `${attachedCount}/${totalCount} attached`,
      success: attachedCount > 0 && attachedCount === totalCount,
    }
  }

  const getReplacedInPACSStatus = (auditLogs) => {
    if (auditLogs.length === 0) {
      return false
    }

    // Find the last audit log with eventType === 'IRPC'
    const irpcLogs = auditLogs.filter((log) => log.eventType === "IRPC")
    if (irpcLogs.length === 0) {
      return false
    }

    const lastIrpcLog = irpcLogs[irpcLogs.length - 1]
    return lastIrpcLog.description === "Yes"
  }

  const getIsReportFinalized = (auditLogs) => {
    if (auditLogs.length === 0) {
      return false
    }

    const finalizedLog = auditLogs.find((log) =>
      ["FARP", "FRRP", "ARRP"].includes(log.eventType)
    )

    const isPendingStatus = finalizedLog?.timestamp
      ? (new Date() - new Date(finalizedLog.timestamp)) / (1000 * 60) < 70
      : false

    return !!finalizedLog && !isPendingStatus
  }

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    // refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAllIllustrationLogs({
    limit: 20,
    sortBy: "createdAt:DESC",
    filters: {
      searchTerm: activeSearchTerm.trim(),
      source,
      bodyPart,
      view:
        bodyPart === bodyParts.SPINE || bodyPart === BREAST_BODY_PART
          ? view
          : undefined,
      dateRange: getDateFilterValue(dateRange),
      status: status === "All" ? undefined : status,
      replacedInPACS:
        source !== sourceTypes.RAMSOFT || replacedInPACS === "All"
          ? undefined
          : replacedInPACS,
    },
  })

  const { data: logsData, isLoading: isLogsLoading } =
    useIllustrationLogsByAccessionNo({
      accessionNo: selectedLogForDrawer?.accessionNumber,
      messageId: selectedLogForDrawer?.reportNumber,
      bodyPart: selectedLogForDrawer?.bodyPart,
      ov3Id: selectedLogForDrawer?.ov3Id,
      source: selectedLogForDrawer?.source,
    })

  useEffect(() => {
    setExpandedRow(null)
  }, [activeSearchTerm, source, bodyPart, dateRange, status, view])

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        rootMargin: "200px",
        threshold: 0.1,
      }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const bodyPartOptions = [
    ...Object.values(bodyParts),
    ...(source === sourceTypes.OUTSIDE_V3 ? [BREAST_BODY_PART] : []),
    WHOLE_BODY_BODY_PART,
  ]
  const dateRangeOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
  ]
  const statusOptions = ["All", "Success", "Errored"]

  const handleClearFilters = () => {
    setSource(sourceTypes.RAMSOFT)
    setBodyPart(bodyParts.SPINE)
    setView("All")
    setDateRange("Today")
    setStatus("All")
    setReplacedInPACS("All")
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  const handleViewReportAndPrompt = () => {
    setSelectedLogForDrawer({
      ...selectedLogForDrawer,
      illustration_logs: logsData,
    })
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-2">New Illustration Logs</h1>

      {/* Filter Row */}
      <div className="bg-gray-50 p-4 rounded-lg mb-2">
        <div className="flex gap-4">
          {/* Source */}
          <div>
            <Dropdown
              disabled={activeSearchTerm && activeSearchTerm.length > 0}
              label="SOURCE"
              options={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? [""]
                  : sourceOptions
              }
              setValue={handleSourceChange}
              title={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? "Clear the search field to enable filters"
                  : "Filter result according to source"
              }
              value={
                activeSearchTerm && activeSearchTerm.length > 0 ? "" : source
              }
            />
          </div>

          {/* Body Part */}
          <div>
            <Dropdown
              disabled={activeSearchTerm && activeSearchTerm.length > 0}
              label="BODY PART"
              options={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? [""]
                  : bodyPartOptions
              }
              setValue={handleBodyPartChange}
              title={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? "Clear the search field to enable filters"
                  : "Filter result according to body part"
              }
              value={
                activeSearchTerm && activeSearchTerm.length > 0 ? "" : bodyPart
              }
            />
          </div>

          {/* View */}
          {bodyPart === bodyParts.SPINE && (
            <div>
              <Dropdown
                disabled={activeSearchTerm && activeSearchTerm.length > 0}
                label="VIEW"
                options={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? [""]
                    : [
                        "All",
                        "Sagittal",
                        "Axial",
                        "Spondylolisthesis",
                        "Dermatomes",
                      ]
                }
                setValue={setView}
                title={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? "Clear the search field to enable filters"
                    : "Filter result according to view"
                }
                value={
                  activeSearchTerm && activeSearchTerm.length > 0 ? "" : view
                }
              />
            </div>
          )}

          {/* Breast View */}
          {bodyPart === BREAST_BODY_PART && (
            <div>
              <Dropdown
                disabled={activeSearchTerm && activeSearchTerm.length > 0}
                label="VIEW"
                options={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? [""]
                    : breastViews
                }
                setValue={setView}
                title={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? "Clear the search field to enable filters"
                    : "Filter result according to view"
                }
                value={
                  activeSearchTerm && activeSearchTerm.length > 0 ? "" : view
                }
              />
            </div>
          )}

          {/* Date Range */}
          <div>
            <Dropdown
              disabled={activeSearchTerm && activeSearchTerm.length > 0}
              label="DATE RANGE"
              options={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? [""]
                  : dateRangeOptions
              }
              setValue={setDateRange}
              title={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? "Clear the search field to enable filters"
                  : "Filter result according to date range"
              }
              value={
                activeSearchTerm && activeSearchTerm.length > 0 ? "" : dateRange
              }
            />
          </div>
          {/* Status */}
          <div>
            <Dropdown
              disabled={activeSearchTerm && activeSearchTerm.length > 0}
              label="STATUS"
              options={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? [""]
                  : statusOptions
              }
              setValue={setStatus}
              title={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? "Clear the search field to enable filters"
                  : "Filter result according to success/error status"
              }
              value={
                activeSearchTerm && activeSearchTerm.length > 0 ? "" : status
              }
            />
          </div>
          {/* Replaced in PACS */}
          {source === sourceTypes.RAMSOFT && (
            <div>
              <Dropdown
                disabled={activeSearchTerm && activeSearchTerm.length > 0}
                label="REPLACED IN PACS"
                options={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? [""]
                    : ["All", "Yes", "No"]
                }
                setValue={setReplacedInPACS}
                title={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? "Clear the search field to enable filters"
                    : "Filter result according to replaced in PACS status"
                }
                value={
                  activeSearchTerm && activeSearchTerm.length > 0
                    ? ""
                    : replacedInPACS
                }
              />
            </div>
          )}
          <div className="flex items-end justify-end">
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              disabled={activeSearchTerm && activeSearchTerm.length > 0}
              title={
                activeSearchTerm && activeSearchTerm.length > 0
                  ? "Clear the search field to enable filters"
                  : "Clear all filters"
              }
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
          <div className="flex-1" />
          {/* Accession Number */}
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                ACCESSION NUMBER
              </label>
              <input
                className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. AN-2026-00142"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
            </div>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-blue-400"
              onClick={() => {
                setActiveSearchTerm(searchTerm)
              }}
            >
              Search
            </button>
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-4">
            <Loader />
            <span className="text-gray-600">Loading studies...</span>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> studies
              </span>
            </div>
            {data && data.length > 0 ? (
              <div className="space-y-2 max-h-[calc(100vh-325px)] overflow-auto">
                {data.map((log) => (
                  <div
                    key={log.bodyPartResultsId}
                    ref={
                      expandedRow === log.bodyPartResultsId
                        ? expandedRowRef
                        : null
                    }
                    className="bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    {/* Main Row */}
                    <div
                      className="flex items-center justify-between p-2.5 hover:cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        toggleRowExpansion(log.bodyPartResultsId)
                        setSelectedLogForDrawer(log)
                      }}
                    >
                      <div className="flex items-center gap-4">
                        {/* Status Indicator */}
                        <div
                          className={`w-2 h-2 rounded-full ${getAttachedValue(log)?.success ? "bg-green-500" : "bg-red-500"}`}
                        />
                        {/* Accession Number */}
                        <span className="font-semibold text-gray-900">
                          {log.accessionNumber}
                        </span>
                        <span className="px-3 py-1 text-[10px] rounded-full flex items-center bg-gray-200 border border-gray-300 uppercase text-gray-700">
                          {getBodyPartLabel(log.bodyPart) || "N/A"}
                        </span>
                        {/*Is Replaced in PACS*/}
                        {log.source?.toLowerCase() === "ramsoft" && (
                          <span
                            className={`px-3 py-1 text-[10px] rounded-full flex items-center gap-1 ${
                              getReplacedInPACSStatus(log.audit_logs)
                                ? "bg-green-100 text-green-800 border border-green-300"
                                : !getIsReportFinalized(log.audit_logs)
                                  ? "bg-orange-100 text-orange-800 border border-orange-300"
                                  : "bg-red-100 text-red-800 border border-red-300"
                            }`}
                          >
                            <FontAwesomeIcon
                              className="w-3 h-3"
                              icon={
                                getReplacedInPACSStatus(log.audit_logs)
                                  ? faCheck
                                  : !getIsReportFinalized(log.audit_logs)
                                    ? faInfoCircle
                                    : faTimes
                              }
                            />
                            {getReplacedInPACSStatus(log.audit_logs)
                              ? "Replaced in PACS"
                              : !getIsReportFinalized(log.audit_logs)
                                ? "PACS Status Pending"
                                : "Not replaced in PACS"}
                          </span>
                        )}
                        {/* Additional Info */}
                        <span
                          className={`text-[10px] text-gray-500 ${getAttachedValue(log)?.success ? "text-green-500" : "text-red-500"}`}
                        >
                          {getAttachedValue(log)?.value}
                        </span>
                      </div>

                      {/* Date with Dropdown */}
                      <div className="flex items-center gap-2 text-gray-500">
                        <span className="text-[10px]">
                          {new Date(log.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <FontAwesomeIcon
                          className={`w-4 h-4 transition-transform ${
                            expandedRow === log.bodyPartResultsId
                              ? "rotate-180"
                              : ""
                          }`}
                          icon={faChevronDown}
                        />
                      </div>
                    </div>

                    {/* Accordion Content */}
                    {expandedRow === log.bodyPartResultsId &&
                      (isLogsLoading ? (
                        <div className="border-t border-gray-200 p-4 bg-gray-50">
                          <div className="flex gap-2 items-center mb-4">
                            <h2 className="text-xs font-medium text-gray-700 uppercase">
                              AI response per view
                            </h2>
                            <div className="w-24 h-6 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3].map((index) => (
                              <div
                                key={index}
                                className="rounded-md bg-gray-100 w-[33%] border border-gray-300"
                              >
                                <div className="flex justify-between items-center p-3 rounded-t-md border bg-gray-200">
                                  <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
                                  <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
                                </div>
                                <div className="p-2 bg-gray-100 rounded-b-md h-24">
                                  <div className="space-y-2">
                                    <div className="w-full h-3 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="w-3/4 h-3 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="w-1/2 h-3 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="w-2/3 h-3 bg-gray-200 rounded animate-pulse"></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="border-t border-gray-200 p-4 bg-gray-50">
                          <div className="flex gap-2 items-center mb-4">
                            <h2 className="text-xs font-medium text-gray-700 uppercase">
                              AI response per view
                            </h2>
                            <button
                              className="text-xs font-medium text-gray-100 bg-primary p-2 rounded-md hover:bg-primary/80 transition-colors border border-gray-300"
                              onClick={() => handleViewReportAndPrompt(log)}
                            >
                              View Report & Prompt
                            </button>
                          </div>
                          {getCardEntries(log).length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {getCardEntries(log).map(
                                ({ result, block }, cardIndex, allCards) => (
                                  <div
                                    key={cardIndex}
                                    className={`rounded-md bg-gray-100 ${allCards.length > 2 ? "w-[33%]" : "w-[49%]"} ${
                                      result.isImageGenerated
                                        ? "border border-green-200"
                                        : result.isNormalReport
                                          ? "border border-gray-300"
                                          : result.isError
                                            ? "border border-red-200"
                                            : "border border-gray-300"
                                    }`}
                                  >
                                    <div
                                      className={`flex justify-between items-center p-3 rounded-t-md border ${
                                        result.isImageGenerated
                                          ? "bg-green-100"
                                          : result.isNormalReport
                                            ? "bg-gray-200"
                                            : result.isError
                                              ? "bg-red-100"
                                              : "bg-gray-200"
                                      }`}
                                    >
                                      <span className="text-sm font-medium text-gray-700">
                                        {result.viewType?.toUpperCase() || ""}
                                        {block.label ? ` — ${block.label}` : ""}
                                      </span>
                                      <span
                                        className={`px-2 py-1 text-sm rounded-full uppercase ${
                                          result.isImageGenerated
                                            ? "text-green-700"
                                            : result.isNormalReport
                                              ? "text-gray-700"
                                              : result.isError
                                                ? "text-red-700"
                                                : "text-gray-700"
                                        }`}
                                      >
                                        {result.isImageGenerated
                                          ? "Illustration attached"
                                          : result.isNormalReport
                                            ? "Normal report - no illustration"
                                            : result.isError
                                              ? result.errorDesc?.length > 70
                                                ? result.errorDesc.substring(
                                                    0,
                                                    70
                                                  ) + "..."
                                                : result.errorDesc
                                              : "No illustration"}
                                      </span>
                                    </div>
                                    {block.data ? (
                                      <div className="p-2 bg-gray-100 rounded-b-md text-lg text-gray-600">
                                        <ReactJson
                                          displayDataTypes={false}
                                          displayObjectSize={false}
                                          enableClipboard={false}
                                          name={null}
                                          src={block.data}
                                        />
                                      </div>
                                    ) : (
                                      <div className="p-2 bg-gray-100 rounded-b-md text-sm italic text-gray-500">
                                        No logs found for this illustration
                                        result
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 italic">
                              No illustration results available
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ))}
                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="h-1" />
                {/* Load More Section - Fallback */}
                {hasNextPage && (
                  <div className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more studies...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                        disabled={isFetchingNextPage}
                        onClick={fetchNextPage}
                      >
                        Load More Studies
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No logs found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report Drawer */}
      <ReportDrawer
        isOpen={isDrawerOpen}
        logData={selectedLogForDrawer}
        onClose={handleCloseDrawer}
      />
    </div>
  )
}

export default NewLogs
